/**
 * 売上と残高（/money/:mode/earnings）。サーバー: GET /v1/money/balance・/summary・/payouts・/statement.csv（money/routes.ts）。
 *
 * 見せる順（MASTER-PLAN「AI ネイティブ運用＝管理画面はほぼ見ないが、見るときは分かりやすく」）:
 *   1. 今いくらあって、いつ出金できるか（保留中 → 確定 → 送金中 → 出金済み の 4 枚。台帳の勘定と同じ並び = お金の流れの順）
 *   2. 出金できないなら、その理由と次の一手（受け取りの登録へ）
 *   3. どこから来たか（収入源別・ゲーム別）
 *   4. 出金の履歴と明細の CSV
 * 準備金（reserve）は 0023 で新規の率を 0 にした（founder 確定）ので、0 のときはタイルを出さない（仕組みが戻ったら自然に出る）。
 * 自分の支払い（自分のゲームで自分が払った分・売上に含めない・0252）: 0 でなければタイル・ゲーム別の注記・一覧のカード（SelfPurchasesCard）で見せる。
 *   収入源の表からは除く（売上として数えたように読めるため = moneyDisplay.ts earningSources）。サーバーは GET /v1/money/self-purchases
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { downloadFile, type ApiError } from '../../api';
import { ago, Empty, ErrorState, fullDate, num, PageHeader, Spinner, StatTile } from '../../components/ui';
import { usePagedResource, useResource, type MoreState } from '../../hooks';
import { LoadMore } from '../../components/LoadMore';
import { errorText, translate, useT, type MessageKey } from '../../i18n';
import { money } from '../../money';
import type { BalanceView, EarningsSummary, PayoutRow, SelfPurchaseRow } from '../../moneyApi';
import { blockReasons, payoutTone } from '../../moneyLogic';
import { earningSources, selfPurchaseAmount, selfPurchaseStatusKey, showSelfPurchases } from './moneyDisplay';
import { useMoney } from './MoneyShell';

const SOURCE_KEYS: Record<string, MessageKey> = {
  sale: 'earn.source.sale',
  ad: 'earn.source.ad',
  issuer_reward: 'earn.source.issuer_reward',
  pass: 'earn.source.pass',
  self_purchase: 'earn.source.self_purchase'
};
const PAYOUT_KEYS: Record<string, MessageKey> = {
  draft: 'payout.status.draft',
  approved: 'payout.status.approved',
  transferring: 'payout.status.transferring',
  paid: 'payout.status.paid',
  failed: 'payout.status.failed',
  reversed: 'payout.status.reversed',
  cancelled: 'payout.status.cancelled'
};
/** 未知の値（サーバーが状態を足した）はそのまま出す = 画面が壊れない */
export const payoutStatusLabel = (s: string) => (PAYOUT_KEYS[s] ? translate(PAYOUT_KEYS[s]) : s);

export function EarningsPage() {
  const { mode, livemode } = useMoney();
  const q = `livemode=${livemode}`;
  const bal = useResource<BalanceView>(`/v1/money/balance?${q}`);
  const sum = useResource<EarningsSummary>(`/v1/money/summary?${q}`);
  // 20 件ずつ「さらに読み込む」で古い払い出しへ（2026-10-04 console/limits-ui。サーバーには cursor があったが画面が使っていなかった）
  const payouts = usePagedResource<{ payouts: PayoutRow[]; nextCursor?: string | null }, 'payouts'>(`/v1/money/payouts?${q}&limit=20`, 'payouts');
  const selfList = useResource<{ selfPurchases: SelfPurchaseRow[] }>(`/v1/money/self-purchases?${q}`);
  const { t } = useT();

  const b = bal.data?.balances;
  const cur = bal.data?.currency ?? 'usd';
  const canWithdraw = !!bal.data?.payee.canWithdraw;
  const reasons = blockReasons(bal.data?.payee.reasons ?? []);
  const sources = earningSources(sum.data?.bySource ?? []);
  const selfBalance = b?.selfPurchase ?? 0;
  const selfRows = selfList.data?.selfPurchases ?? [];
  // 一覧の取得に失敗しても（古いサーバー = route_missing 等）売上の画面は止めない。残高・ゲーム別の数字だけで出せるときは出す
  const showSelf = showSelfPurchases(selfBalance, selfRows.length, sum.data?.byGame);
  const hasAnything =
    !!sum.data && (sources.length > 0 || showSelf || (b && (b.pending || b.available || b.inTransit || b.paidLifetime)));

  return (
    <>
      <PageHeader eyebrow={t(`money.mode.${mode}`)} title={t('earn.title')}>
        {bal.data &&
          (canWithdraw ? (
            <Link to={`/money/${mode}/withdraw`} className="btn btn-primary">
              {t('earn.withdraw')}
            </Link>
          ) : (
            <Link to={`/money/${mode}/get-paid`} className="btn btn-ghost">
              {t('earn.setUp')}
            </Link>
          ))}
      </PageHeader>
      <p className="page-lead muted">{t('earn.lead')}</p>

      {bal.loading && !bal.data && <Spinner />}
      {bal.error && <ErrorState error={bal.error} what={t('earn.title')} onRetry={bal.reload} />}

      {b && (
        <>
          <section className="tiles money-flow" aria-label={t('earn.title')}>
            <StatTile label={t('earn.pending')} value={money(b.pending, cur)} hint={t('earn.pendingHint')} />
            {b.reserve !== 0 && <StatTile label={t('earn.reserve')} value={money(b.reserve, cur)} />}
            <StatTile
              label={t('earn.available')}
              value={money(b.available, cur)}
              hint={b.available < 0 ? t('earn.owedHint') : canWithdraw ? t('earn.availableHint') : t('earn.availableHintSetup')}
              tone={b.available < 0 ? 'warn' : 'accent'}
            />
            <StatTile label={t('earn.inTransit')} value={money(b.inTransit, cur)} hint={t('earn.inTransitHint')} />
            <StatTile label={t('earn.paid')} value={money(b.paidLifetime, cur)} hint={t('earn.paidHint')} />
            {/* 0 のときは出さない（ほとんどの人には関係が無い）。出金できる額（確定済み）とは別の勘定 */}
            {selfBalance > 0 && <StatTile label={t('earn.self.tile')} value={money(selfBalance, cur)} hint={t('earn.self.tileHint')} />}
          </section>

          {!canWithdraw && reasons.length > 0 && (
            <div className="alert alert-info" role="note">
              <span>{t('earn.notReady', { reason: reasons.map((r) => t(`wd.reason.${r}`)).join(' · ') })}</span>
              <Link to={`/money/${mode}/get-paid`} className="btn btn-ghost btn-sm">
                {t('wd.goSetup')}
              </Link>
            </div>
          )}
        </>
      )}

      {sum.error && <ErrorState error={sum.error} what={t('earn.bySource')} onRetry={sum.reload} />}
      {sum.data && !hasAnything && (
        <section className="card">
          <Empty title={t('earn.emptyTitle')}>
            <p>{mode === 'test' ? t('earn.emptyBodyTest') : t('earn.emptyBody')}</p>
          </Empty>
        </section>
      )}
      {sum.data && sources.length > 0 && (
        <div className="money-grid">
          <section className="card">
            <h2 className="card-title">{t('earn.bySource')}</h2>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('earn.col.source')}</th>
                    <th className="num">{t('earn.col.count')}</th>
                    <th className="num">{t('earn.col.reversed')}</th>
                    <th className="num">{t('earn.col.net')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map((s) => (
                    <tr key={s.source}>
                      <td>{SOURCE_KEYS[s.source] ? t(SOURCE_KEYS[s.source]!) : s.source}</td>
                      <td className="num">{num(s.count)}</td>
                      <td className="num muted">{s.reversed ? `−${money(s.reversed, sum.data!.currency)}` : '—'}</td>
                      <td className="num">
                        <strong>{money(s.net, sum.data!.currency)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section className="card">
            <h2 className="card-title">{t('earn.byGame')}</h2>
            {sum.data.byGame.length === 0 ? (
              <p className="muted small">—</p>
            ) : (
              <ul className="bar-list">
                {sum.data.byGame.map((g) => {
                  const total = g.sales + (g.issuerRewards ?? 0);
                  const max = Math.max(...sum.data!.byGame.map((x) => x.sales + (x.issuerRewards ?? 0)), 1);
                  return (
                    <li key={g.projectId}>
                      <div className="bar-list-row">
                        {g.name ? (
                          <Link to={`/p/${g.projectId}/${mode === 'live' ? 'production' : 'development'}/payments`}>{g.name}</Link>
                        ) : (
                          <span className="muted">{t('earn.unknownGame')}</span>
                        )}
                        <span className="mono">{money(total, sum.data!.currency)}</span>
                      </div>
                      <div className="meter" aria-hidden="true">
                        <span style={{ width: `${Math.max(2, (total / max) * 100)}%` }} />
                      </div>
                      {/* 棒と合計は売上だけ。自分の支払いは足さずに注記する（売上に含めない） */}
                      {(g.selfPurchases ?? 0) > 0 && (
                        <span className="muted small">{t('earn.self.byGame', { amount: money(g.selfPurchases!, sum.data!.currency) })}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      )}

      {showSelf && <SelfPurchasesCard rows={selfList.data ? selfRows : null} />}

      <PayoutsCard rows={payouts.data?.payouts ?? null} error={payouts.error} reload={payouts.reload} more={payouts.more} livemode={livemode} />
    </>
  );
}

/**
 * 自分の支払い（売上に含めない）の一覧。説明の文は founder の言い回しのまま（「あなたのカード・お名前で…誤りならサポートへ」）。
 * WHY 判定の材料（matchedBy: カードの一致・氏名の一致）を出さないか: どの条件で判定しているかを広く見せない（抜け道の手がかりにしない）。
 * 誤判定の人はサポートに連絡すれば運営が解除する（POST /v1/staff/money/self-purchases/:id/release）ので、材料が見えなくても困らない。
 * rows = null（一覧の取得に失敗・古いサーバー）のときは説明だけ出す（タイル・ゲーム別の数字の意味は分かる）
 */
function SelfPurchasesCard({ rows }: { rows: SelfPurchaseRow[] | null }) {
  const { t } = useT();
  return (
    <section className="card">
      <h2 className="card-title">{t('earn.self.title')}</h2>
      <p className="muted small">{t('earn.self.body')}</p>
      {rows && rows.length === 0 && <p className="muted small">{t('earn.self.none')}</p>}
      {rows && rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t('earn.self.col.when')}</th>
                <th>{t('earn.self.col.game')}</th>
                <th className="num">{t('earn.self.col.paid')}</th>
                <th className="num">{t('earn.self.col.amount')}</th>
                <th>{t('earn.self.col.status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const key = selfPurchaseStatusKey(r.status);
                return (
                  <tr key={r.purchaseId}>
                    <td title={fullDate(r.createdAt)}>{ago(r.createdAt)}</td>
                    <td>{r.projectName ?? <span className="muted">{t('earn.unknownGame')}</span>}</td>
                    <td className="num muted">{money(r.gross, r.currency)}</td>
                    <td className="num">{money(selfPurchaseAmount(r), r.currency)}</td>
                    <td>
                      <span className={`pill ${key === 'prepaid' ? 'pill-accent' : ''}`}>{key ? t(`earn.self.status.${key}`) : r.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PayoutsCard({ rows, error, reload, more, livemode }: { rows: PayoutRow[] | null; error: ApiError | null; reload: () => void; more: MoreState; livemode: boolean }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // 明細: 直近 12 か月（サーバーの上限は 1 年）。to は明日の 0 時（今日の分まで入る）
  const download = async () => {
    setBusy(true);
    setErr(null);
    const to = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
    const from = new Date(Date.now() - 365 * 86_400_000).toISOString().slice(0, 10);
    try {
      await downloadFile(`/v1/money/statement.csv?livemode=${livemode}&from=${from}&to=${to}`, `kumo-statement-${from}-${to}.csv`);
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">{t('earn.payouts')}</h2>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => void download()} disabled={busy} title={t('earn.statementHint')}>
          {busy ? t('common.working') : t('earn.statement')}
        </button>
      </div>
      {err && (
        <p className="alert alert-error" role="alert">
          {err}
        </p>
      )}
      {error && <ErrorState error={error} what={t('earn.payouts')} onRetry={reload} />}
      {rows && rows.length === 0 && <p className="muted small">{t('earn.noPayouts')}</p>}
      {rows && rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t('earn.col.requested')}</th>
                <th className="num">{t('earn.col.amount')}</th>
                <th className="num">{t('earn.col.fee')}</th>
                <th>{t('earn.col.status')}</th>
                <th>{t('earn.col.via')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td title={fullDate(p.createdAt)}>{ago(p.createdAt)}</td>
                  <td className="num">
                    <strong>{money(p.amount, p.currency)}</strong>
                    {p.method === 'instant' && <span className="pill pill-accent">{t('earn.instant')}</span>}
                  </td>
                  <td className="num muted">{money(p.fee, p.currency)}</td>
                  <td>
                    <span className={`pill ${payoutTone(p.status) === 'neutral' ? '' : `pill-${payoutTone(p.status)}`}`}>{payoutStatusLabel(p.status)}</span>
                    {p.failureCode && <span className="muted small"> {p.failureCode}</span>}
                  </td>
                  <td className="small">{p.requestedVia === 'mcp' ? t('earn.via.mcp') : t('earn.via.dashboard')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMore more={more} />
    </section>
  );
}
